import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { addCorporateIdCard, editCorporateIdCard } from '../../../../api/endpoints/corporateEmployee';
import type { CorporateIDCard } from '../../../../api/types/corporateEmployee';
import { ApiErrorAlert } from '../../../../components/ApiErrorAlert';

const schema = z.object({
  cardNumber: z.string().min(1, 'Kart numarası gerekli'),
  country: z.string().min(1, 'Ülke gerekli'),
});

type FormValues = z.infer<typeof schema>;

const emptyValues: FormValues = { cardNumber: '', country: '' };

/** Add/edit dialog for a Kimlik Kartı sub-record. NOTE: the real API's
 *  EditCorporateIDCard behaves as delete-old+insert-new — the record comes back under a new
 *  `cardID` after saving, which is fine here since the parent list is refetched on success. */
export function IdCardFormDialog({
  open,
  employeeID,
  card,
  onClose,
}: {
  open: boolean;
  employeeID: number;
  /** null = create mode; otherwise edit mode for this row. */
  card: CorporateIDCard | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!card;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: emptyValues });

  useEffect(() => {
    if (open) reset(card ? { cardNumber: card.cardNumber ?? '', country: card.country ?? '' } : emptyValues);
  }, [open, card, reset]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      isEdit && card
        ? editCorporateIdCard({ ...card, ...values })
        : addCorporateIdCard({ employeeID, ...values }),
    onSuccess: () => {
      enqueueSnackbar(isEdit ? 'Kimlik kartı güncellendi.' : 'Kimlik kartı eklendi.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['corporateEmployee', 'idCards', employeeID] });
      onClose();
    },
    onError: () => {
      /* surfaced via ApiErrorAlert below */
    },
  });

  const submit = handleSubmit((values) => mutation.mutate(values));

  return (
    <Dialog open={open} onClose={() => !mutation.isPending && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>{isEdit ? 'Kimlik Kartını Güncelle' : 'Yeni Kimlik Kartı'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                size="small"
                label="Kart Numarası"
                error={!!errors.cardNumber}
                helperText={errors.cardNumber?.message}
                {...register('cardNumber')}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                size="small"
                label="Ülke"
                error={!!errors.country}
                helperText={errors.country?.message}
                {...register('country')}
              />
            </Grid>
          </Grid>
          <ApiErrorAlert error={mutation.error} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Vazgeç
        </Button>
        <Button onClick={submit} variant="contained" disabled={mutation.isPending}>
          {mutation.isPending ? 'Kaydediliyor…' : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
